import { useState, useEffect, useCallback } from "react";
import {
    RefreshCw, Search, ChevronDown, ChevronLeft, ChevronRight, Download,
    Building2, Loader2, Info,
} from "lucide-react";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { cn } from "../../utils/tailwindUtils";
import useApiMethods from "../../lib/useApiMethods";
import apiClient from "../../lib/apiClient";
import { ONBOARDED_CLIENT_LIST_REPORT_URL, USERS_SIMPLE_URL } from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";
import { useNavigate } from "react-router-dom";
import { usePersistedState } from "../../hooks/usePersistedState";
import TruncatedText from "../../components/ui/truncated-text";
import dayjs from "dayjs";

const DATE_PRESETS = [
    { value: "all",        label: "All Time" },
    { value: "this_month", label: "This Month" },
    { value: "last_month", label: "Last 30 Days" },
    { value: "this_year",  label: "This Year" },
    { value: "custom",     label: "Custom Range" },
];

const resolveDateRange = (preset, customFrom, customTo) => {
    const today = dayjs();
    const fmt = d => d.format("DD-MM-YYYY");
    switch (preset) {
        case "this_month": return { from_date: fmt(today.startOf("month")), to_date: fmt(today) };
        case "last_month": return { from_date: fmt(today.subtract(30, "day")), to_date: fmt(today) };
        case "this_year":  return { from_date: fmt(today.startOf("year")), to_date: fmt(today) };
        case "custom":     return (customFrom && customTo)
            ? { from_date: dayjs(customFrom).format("DD-MM-YYYY"),
                to_date: dayjs(customTo).format("DD-MM-YYYY") }
            : {};
        default: return {};
    }
};

const money = (n) =>
    `₹${(Number(n) || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const num = (n) => (n ?? 0).toLocaleString("en-IN");

const STATUS_STYLE = {
    "In Progress": "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
    "Go Live":     "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
    "Completed":   "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
    "Suspended":   "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
};

const Tile = ({ label, value, sub }) => (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-3">
        <p className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 font-medium">{label}</p>
        <p className="text-xl font-bold text-gray-900 dark:text-white mt-0.5 tabular-nums">{value}</p>
        {sub && <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">{sub}</p>}
    </div>
);

export default function OnboardedClientListReport() {
    const { fetch } = useApiMethods();
    const navigate = useNavigate();

    const [datePreset, setDatePreset] = usePersistedState("ocl_preset", "all");
    const [customFrom, setCustomFrom] = usePersistedState("ocl_from", "");
    const [customTo,   setCustomTo]   = usePersistedState("ocl_to", "");
    const [statusFilter, setStatusFilter] = usePersistedState("ocl_status", "");
    const [contactFilter, setContactFilter] = usePersistedState("ocl_contact", "");
    const [selectedUserId, setSelectedUserId] = usePersistedState("ocl_user", "");

    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);

    const [resp, setResp] = useState(null);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        fetch(USERS_SIMPLE_URL)
            .then(r => { if (r?.status === "success") setUsers(r.data || []); })
            .catch(() => {});
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const params = useCallback((extra = {}) => {
        const p = new URLSearchParams({
            ...resolveDateRange(datePreset, customFrom, customTo),
            ...extra,
        });
        if (statusFilter) p.set("status", statusFilter);
        if (contactFilter) p.set("has_contact", contactFilter);
        if (selectedUserId) p.set("assigned_user_id", selectedUserId);
        if (search) p.set("search", search);
        return p;
    }, [datePreset, customFrom, customTo, statusFilter, contactFilter,
        selectedUserId, search]);

    const load = useCallback(() => {
        setLoading(true);
        const p = params({ page: String(page), page_size: String(pageSize) });
        fetch(`${ONBOARDED_CLIENT_LIST_REPORT_URL}?${p.toString()}`)
            .then(r => {
                if (r?.status === "success") setResp(r);
                else ToastMsg(r?.message || "Failed to load report", "error");
            })
            .catch(() => ToastMsg("Failed to load report", "error"))
            .finally(() => setLoading(false));
    }, [params, page, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);
    useEffect(() => { setPage(1); },
        [datePreset, customFrom, customTo, statusFilter, contactFilter,
         selectedUserId, search]);

    const exportExcel = async () => {
        setExporting(true);
        try {
            const p = params({ export: "excel" });
            const res = await apiClient.get(
                `${ONBOARDED_CLIENT_LIST_REPORT_URL}?${p.toString()}`,
                { responseType: "blob" });
            const blob = new Blob([res], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `onboarded-clients-${dayjs().format("YYYY-MM-DD")}.xlsx`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            ToastMsg("Export failed", "error");
        } finally {
            setExporting(false);
        }
    };

    const s = resp?.summary;
    const rows = resp?.data || [];
    const totalPages = resp?.total_pages || 1;
    const totalCount = resp?.total_count || 0;
    const hasFilters = datePreset !== "all" || statusFilter || contactFilter
        || selectedUserId || search;

    return (
        <ContainerCard>
            <div className="flex items-center justify-between mb-4 px-1">
                <div>
                    <h1 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-primary" />
                        Onboarded Client List
                    </h1>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                        Every onboarded client with its value, contact and location — one row each
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button onClick={exportExcel} size="sm" variant="outline"
                        className="text-xs" disabled={exporting || !totalCount}>
                        {exporting ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                   : <Download className="h-3 w-3 mr-1" />}
                        Excel
                    </Button>
                    <Button onClick={load} size="sm" variant="outline"
                        className="flex items-center gap-1 text-xs" disabled={loading}>
                        <RefreshCw className={cn("h-3 w-3", loading && "animate-spin")} />
                        Refresh
                    </Button>
                </div>
            </div>

            {/* ── filters ── */}
            <div className="flex flex-wrap items-center gap-2 mb-3">
                <div className="relative">
                    <select value={datePreset}
                        onChange={e => {
                            setDatePreset(e.target.value);
                            if (e.target.value !== "custom") { setCustomFrom(""); setCustomTo(""); }
                        }}
                        className="appearance-none pl-3 pr-8 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-indigo-400 cursor-pointer">
                        {DATE_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                </div>

                {datePreset === "custom" && (
                    <>
                        <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
                            className="text-xs border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300" />
                        <span className="text-xs text-gray-400">to</span>
                        <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
                            className="text-xs border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300" />
                    </>
                )}

                <div className="relative">
                    <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                        className="appearance-none pl-3 pr-8 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 cursor-pointer">
                        <option value="">All Statuses</option>
                        {(resp?.statuses || []).map(st => <option key={st} value={st}>{st}</option>)}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                </div>

                <div className="relative">
                    <select value={contactFilter} onChange={e => setContactFilter(e.target.value)}
                        className="appearance-none pl-3 pr-8 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 cursor-pointer">
                        <option value="">Contact: any</option>
                        <option value="yes">Has a contact</option>
                        <option value="no">Missing a contact</option>
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                </div>

                <div className="relative">
                    <select value={selectedUserId} onChange={e => setSelectedUserId(e.target.value)}
                        className="appearance-none pl-3 pr-8 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 cursor-pointer">
                        <option value="">All Owners</option>
                        {users.map(u => {
                            const name = [u.login_fname, u.login_lname].filter(Boolean).join(" ") || u.login_name;
                            return <option key={u.id} value={u.id}>{name}</option>;
                        })}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                </div>

                <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                    <input value={searchInput}
                        onChange={e => setSearchInput(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") setSearch(searchInput.trim()); }}
                        onBlur={() => setSearch(searchInput.trim())}
                        placeholder="Name, contact, phone, city…"
                        className="pl-8 pr-3 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-indigo-400 w-52" />
                </div>

                {hasFilters && (
                    <button onClick={() => {
                        setDatePreset("all"); setCustomFrom(""); setCustomTo("");
                        setStatusFilter(""); setContactFilter(""); setSelectedUserId("");
                        setSearchInput(""); setSearch("");
                    }} className="text-xs text-indigo-500 hover:text-indigo-700">Clear</button>
                )}
            </div>

            {/* ── summary ── */}
            {s && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                    <Tile label="Clients" value={num(s.clients)}
                        sub={s.missing_date ? `${s.missing_date} with no onboarded date` : "all dated"} />
                    <Tile label="Total value" value={money(s.total_value)}
                        sub={`${s.with_value} of ${s.clients} have a value`} />
                    <Tile label="With a contact" value={num(s.with_contact)}
                        sub={`${s.with_phone} phone · ${s.with_email} email`} />
                    <Tile label="With remarks" value={num(s.with_remarks)}
                        sub={`${s.clients - s.with_remarks} blank`} />
                </div>
            )}

            {/* Coverage is stated plainly: a column full of dashes should read as
                missing data, not as a broken report. */}
            {s && s.clients > 0 && s.with_contact < s.clients && (
                <div className="flex items-start gap-2 rounded-lg p-2.5 mb-3 text-[11px]
                                bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300">
                    <Info className="h-3.5 w-3.5 mt-px shrink-0" />
                    <span>
                        {s.clients - s.with_contact} of {s.clients} clients have no contact on
                        file, so those rows show a dash rather than a name or number. Filter to
                        <strong className="mx-1">Missing a contact</strong>to work through them.
                    </span>
                </div>
            )}

            {/* ── table ── */}
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                <table className="w-full text-xs">
                    <thead className="bg-gray-50 dark:bg-gray-800/60">
                        <tr className="text-gray-500 dark:text-gray-400">
                            <th className="px-3 py-2.5 text-left font-medium">#</th>
                            <th className="px-3 py-2.5 text-left font-medium min-w-[180px]">Customer Name</th>
                            <th className="px-3 py-2.5 text-left font-medium whitespace-nowrap">Onboarded Date</th>
                            <th className="px-3 py-2.5 text-right font-medium">Value</th>
                            <th className="px-3 py-2.5 text-left font-medium">Contact Name</th>
                            <th className="px-3 py-2.5 text-left font-medium">Phone No</th>
                            <th className="px-3 py-2.5 text-left font-medium">Email</th>
                            <th className="px-3 py-2.5 text-left font-medium">Location</th>
                            <th className="px-3 py-2.5 text-left font-medium min-w-[200px]">Remarks</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                        {rows.map((row, idx) => (
                            <tr key={row.id}
                                onClick={() => navigate(`/onboarded/${row.id}`)}
                                className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50">
                                <td className="px-3 py-2.5 text-gray-400 dark:text-gray-500">
                                    {(page - 1) * pageSize + idx + 1}
                                </td>
                                <td className="px-3 py-2.5 text-gray-800 dark:text-gray-200 font-medium">
                                    <TruncatedText text={row.customer_name} />
                                    {row.onboarding_status && (
                                        <span className={cn("ml-2 px-1.5 py-0.5 rounded-full text-[9px] font-medium",
                                            STATUS_STYLE[row.onboarding_status]
                                                || "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300")}>
                                            {row.onboarding_status}
                                        </span>
                                    )}
                                </td>
                                <td className="px-3 py-2.5 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                                    {row.onboarded_date || "—"}
                                </td>
                                <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-gray-900 dark:text-white whitespace-nowrap">
                                    {row.value ? money(row.value) : "—"}
                                </td>
                                <td className="px-3 py-2.5 text-gray-600 dark:text-gray-400">
                                    {row.contact_name || <span className="text-gray-300 dark:text-gray-600">—</span>}
                                </td>
                                <td className="px-3 py-2.5 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                                    {row.contact_number || <span className="text-gray-300 dark:text-gray-600">—</span>}
                                </td>
                                <td className="px-3 py-2.5 text-gray-600 dark:text-gray-400">
                                    {row.email
                                        ? <TruncatedText text={row.email} />
                                        : <span className="text-gray-300 dark:text-gray-600">—</span>}
                                </td>
                                <td className="px-3 py-2.5 text-gray-600 dark:text-gray-400">
                                    {row.location || <span className="text-gray-300 dark:text-gray-600">—</span>}
                                </td>
                                <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400">
                                    {row.remarks
                                        ? <TruncatedText text={row.remarks} lines={2} />
                                        : <span className="text-gray-300 dark:text-gray-600">—</span>}
                                </td>
                            </tr>
                        ))}
                        {!rows.length && (
                            <tr><td colSpan={9} className="px-3 py-10 text-center text-gray-400">
                                {loading ? "Loading…" : "No onboarded clients match these filters."}
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* ── paging ── */}
            <div className="flex flex-wrap items-center justify-between gap-3 mt-3 px-1">
                <div className="flex items-center gap-2">
                    <select value={pageSize}
                        onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                        className="text-[10px] border border-gray-200 dark:border-gray-700 rounded px-1.5 py-0.5 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                        {[25, 50, 100, 200].map(n => <option key={n} value={n}>{n} / page</option>)}
                    </select>
                    <span className="text-[10px] text-gray-400">
                        {totalCount === 0 ? "0"
                            : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, totalCount)}`} of {num(totalCount)}
                    </span>
                </div>
                <div className="flex items-center gap-1">
                    <button onClick={() => setPage(1)} disabled={page === 1}
                        className="px-1.5 py-0.5 text-[10px] rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 text-gray-600 dark:text-gray-300">«</button>
                    <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                        className="p-0.5 rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 text-gray-600 dark:text-gray-300">
                        <ChevronLeft className="h-3 w-3" />
                    </button>
                    <span className="text-[10px] text-gray-500 dark:text-gray-400 px-1.5 tabular-nums">
                        {page} / {totalPages}
                    </span>
                    <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                        className="p-0.5 rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 text-gray-600 dark:text-gray-300">
                        <ChevronRight className="h-3 w-3" />
                    </button>
                    <button onClick={() => setPage(totalPages)} disabled={page >= totalPages}
                        className="px-1.5 py-0.5 text-[10px] rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 text-gray-600 dark:text-gray-300">»</button>
                </div>
            </div>
        </ContainerCard>
    );
}
